import React, { useState, useEffect } from 'react';
import {
  UtensilsCrossed,
  ShoppingCart,
  CalendarDays,
  BarChart3,
  GraduationCap,
  UserCircle,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Clock,
  Globe,
  Smartphone,
} from 'lucide-react';

interface UseCase {
  id: string;
  icon: React.ElementType;
  title: string;
  tagline: string;
  painPoint: string;
  solution: string;
  routingRules: Array<{
    condition: string;
    destination: string;
    icon: React.ElementType;
  }>;
}

const useCases: UseCase[] = [
  {
    id: 'restaurants',
    icon: UtensilsCrossed,
    title: 'Restaurants',
    tagline: 'Dynamic menus that change with the clock',
    painPoint: 'Printing new menus every time prices change or seasonal items rotate is expensive and wasteful.',
    solution: 'One QR code on each table that shows the right menu based on time of day.',
    routingRules: [
      { condition: '6am - 11am', destination: 'breakfast-menu.com', icon: Clock },
      { condition: '11am - 4pm', destination: 'lunch-menu.com', icon: Clock },
      { condition: '4pm - 10pm', destination: 'dinner-menu.com', icon: Clock },
      { condition: 'After 10pm', destination: 'late-night.com', icon: Clock },
    ],
  },
  {
    id: 'retail',
    icon: ShoppingCart,
    title: 'Retail',
    tagline: 'Product info that adapts by region',
    painPoint: 'Global products need different pricing, languages, and compliance info for each market.',
    solution: 'Scan from anywhere and get the right product page with local pricing and language.',
    routingRules: [
      { condition: 'United States', destination: 'us.store.com/product', icon: Globe },
      { condition: 'European Union', destination: 'eu.store.com/product', icon: Globe },
      { condition: 'Asia Pacific', destination: 'apac.store.com/product', icon: Globe },
      { condition: 'Default', destination: 'global.store.com', icon: Globe },
    ],
  },
  {
    id: 'events',
    icon: CalendarDays,
    title: 'Events',
    tagline: 'Agendas that evolve in real-time',
    painPoint: 'Event schedules change constantly. Printed programs are outdated before the event starts.',
    solution: 'Dynamic QR codes that show live agendas and session changes as they happen.',
    routingRules: [
      { condition: 'Before event', destination: 'event.com/preview', icon: Clock },
      { condition: 'During event', destination: 'event.com/live-agenda', icon: Clock },
      { condition: 'After event', destination: 'event.com/recordings', icon: Clock },
      { condition: 'Mobile device', destination: 'event.com/app', icon: Smartphone },
    ],
  },
  {
    id: 'marketing',
    icon: BarChart3,
    title: 'Marketing',
    tagline: 'A/B test campaigns at scale',
    painPoint: 'Print campaigns are a black box. You never know which creative or landing page works best.',
    solution: 'Split traffic between landing pages to find what converts, then shift to the winner.',
    routingRules: [
      { condition: '50% of scans', destination: 'landing-page-a.com', icon: BarChart3 },
      { condition: '50% of scans', destination: 'landing-page-b.com', icon: BarChart3 },
      { condition: 'Mobile users', destination: 'mobile-landing.com', icon: Smartphone },
      { condition: 'Fallback', destination: 'main-site.com', icon: Globe },
    ],
  },
  {
    id: 'education',
    icon: GraduationCap,
    title: 'Education',
    tagline: 'Course materials that route by class',
    painPoint: 'Different classes need different resources. Managing multiple QR codes per room is a nightmare.',
    solution: 'One QR per classroom that routes students to the right materials based on schedule.',
    routingRules: [
      { condition: 'Mon/Wed 9am', destination: 'lms.edu/calc101', icon: Clock },
      { condition: 'Mon/Wed 2pm', destination: 'lms.edu/physics201', icon: Clock },
      { condition: 'Tue/Thu 10am', destination: 'lms.edu/chem101', icon: Clock },
      { condition: 'Default', destination: 'lms.edu/schedule', icon: GraduationCap },
    ],
  },
  {
    id: 'business-cards',
    icon: UserCircle,
    title: 'Business Cards',
    tagline: 'Personal branding that evolves',
    painPoint: 'Contact info changes, job titles change, but your printed business cards are forever wrong.',
    solution: 'A single QR on your card that always shows your latest info and portfolio.',
    routingRules: [
      { condition: 'Work hours', destination: 'linkedin.com/in/you', icon: Clock },
      { condition: 'After hours', destination: 'yourportfolio.com', icon: Clock },
      { condition: 'Recruiters', destination: 'resume.com/you', icon: UserCircle },
      { condition: 'Default', destination: 'linktr.ee/you', icon: Globe },
    ],
  },
];

export function UseCasesPreview() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-rotate every 5 seconds
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % useCases.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused]);

  const activeCase = useCases[activeIndex];
  const Icon = activeCase.icon;

  const goTo = (index: number) => {
    setActiveIndex(index);
    setIsPaused(true);
    // Resume auto-rotate after 10 seconds of inactivity
    setTimeout(() => setIsPaused(false), 10000);
  };

  const goPrev = () => goTo((activeIndex - 1 + useCases.length) % useCases.length);
  const goNext = () => goTo((activeIndex + 1) % useCases.length);

  return (
    <section className="bg-white min-h-screen flex flex-col justify-center section-padding" data-section="use-cases">
      <div className="container-page">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="label text-[--clinqr-primary] mb-4 font-bold">USE CASES</p>
          <h2 className="heading-1 mb-4 text-[--clinqr-navy]">
            Built for Your Industry
          </h2>
          <p className="body-lg max-w-2xl mx-auto text-[--clinqr-navy]/60">
            Discover how your industry solves real problems with dynamic QR codes.
          </p>
        </div>

        {/* Carousel Container */}
        <div
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Main Card */}
          <div className="bg-white rounded-3xl border border-[--clinqr-navy]/10 shadow-2xl overflow-hidden">
            <div className="grid lg:grid-cols-2">
              {/* Left: Industry Info */}
              <div className="p-8 sm:p-12 flex flex-col">
                {/* Icon + Title */}
                <div className="flex items-center gap-5 mb-8">
                  <div className="w-16 h-16 rounded-2xl bg-[--clinqr-navy] flex items-center justify-center shadow-lg">
                    <Icon className="w-8 h-8 text-[--clinqr-lime]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="heading-3 text-[--clinqr-navy]">{activeCase.title}</h3>
                    <p className="text-[--clinqr-primary] font-bold tracking-tight">{activeCase.tagline}</p>
                  </div>
                </div>

                {/* Pain Point */}
                <div className="mb-8">
                  <p className="label mb-2 text-[--clinqr-navy]/40 font-bold">The Problem</p>
                  <p className="text-[--clinqr-navy]/60 text-lg leading-relaxed">{activeCase.painPoint}</p>
                </div>

                {/* Solution */}
                <div className="mb-10">
                  <p className="label mb-2 text-[--clinqr-navy]/40 font-bold">The Solution</p>
                  <p className="text-[--clinqr-navy] text-lg font-medium leading-relaxed">{activeCase.solution}</p>
                </div>

                {/* Navigation Dots */}
                <div className="flex items-center gap-2 mt-auto pt-8 border-t border-[--clinqr-navy]/5">
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      onClick={goPrev}
                      className="w-12 h-12 rounded-full bg-[--clinqr-navy]/5 hover:bg-gray-200 flex items-center justify-center transition-all"
                      aria-label="Previous"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={goNext}
                      className="w-12 h-12 rounded-full bg-[--clinqr-navy]/5 hover:bg-gray-200 flex items-center justify-center transition-all"
                      aria-label="Next"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Right: Routing Example */}
              <div className="bg-[--clinqr-navy] p-8 sm:p-12 flex flex-col justify-center">
                <p className="label text-[--clinqr-lime]/50 mb-8 font-bold">Live Routing Example</p>

                <div className="space-y-4">
                  {activeCase.routingRules.map((rule, index) => {
                    const RuleIcon = rule.icon;
                    return (
                      <div
                        key={index}
                        className="flex items-center gap-4 bg-white/5 rounded-2xl p-5 border border-white/10 transition-all duration-300 hover:bg-white/10 hover:scale-[1.02]"
                        style={{
                          animationDelay: `${index * 100}ms`,
                        }}
                      >
                        <div className="w-10 h-10 rounded-xl bg-[--clinqr-lime] flex items-center justify-center flex-shrink-0">
                          <RuleIcon className="w-5 h-5 text-[--clinqr-navy]" />
                        </div>
                        <span className="text-sm text-white/90 font-medium flex-1">{rule.condition}</span>
                        <ArrowRight className="w-4 h-4 text-white/30" />
                        <code className="text-xs font-mono text-[--clinqr-lime] bg-[--clinqr-lime]/10 px-3 py-1.5 rounded-lg border border-[--clinqr-lime]/20">
                          {rule.destination}
                        </code>
                      </div>
                    );
                  })}
                </div>

                {/* Progress bar */}
                <div className="mt-10">
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[--clinqr-lime] rounded-full transition-all ease-linear"
                      style={{
                        width: isPaused ? '0%' : '100%',
                        transitionDuration: isPaused ? '0ms' : '5000ms',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Industry Icons - Quick Navigation */}
          <div className="flex justify-center gap-4 mt-12">
            {useCases.map((useCase, index) => {
              const UseCaseIcon = useCase.icon;
              const isActive = index === activeIndex;

              return (
                <button
                  key={useCase.id}
                  onClick={() => goTo(index)}
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                    isActive
                      ? 'bg-[--clinqr-navy] text-[--clinqr-lime] shadow-xl scale-110'
                      : 'bg-white text-[--clinqr-navy]/40 border border-[--clinqr-navy]/10 hover:border-[--clinqr-navy]/30 hover:text-[--clinqr-navy]'
                  }`}
                  aria-label={useCase.title}
                >
                  <UseCaseIcon className="w-6 h-6" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
